import type { ClientOrganizationPushNotificationSetting } from '@DigitaleDoerfer/digitale-doerfer-api';
import { useQueryClient } from '@tanstack/react-query';

import { useGrapevineOrganizationApi } from '@/features/api/hooks/grapevine-organization/useGrapevineOrganizationApi';
import { useApiMutation } from '@/features/api/hooks/useApiMutation';
import { getOrganizationCommentPushSettingQueryKey } from '@/features/profile-setting/utils/notificationSettingCache';

type UpdateVariables = {
  organizationId: string;
  commentPushEnabled: boolean;
};

export const useUpdateCommentPushSetting = () => {
  const queryClient = useQueryClient();
  const api = useGrapevineOrganizationApi();

  return useApiMutation<
    ClientOrganizationPushNotificationSetting,
    unknown,
    UpdateVariables
  >({
    mutationFn: (variables) =>
      api.updateCommentPushSetting({
        organizationId: variables.organizationId,
        clientOrganizationCommentPushSettingUpdateRequest: {
          commentPushEnabled: variables.commentPushEnabled
        }
      }),
    onSuccess: (data, variables) => {
      const queryKey = getOrganizationCommentPushSettingQueryKey(
        variables.organizationId
      );
      const currentSetting =
        queryClient.getQueryData<ClientOrganizationPushNotificationSetting>(
          queryKey
        );

      if (currentSetting?.commentPushEnabled === variables.commentPushEnabled) {
        queryClient.setQueryData(queryKey, data);
      }
    },
    onError: (_error, variables) => {
      const queryKey = getOrganizationCommentPushSettingQueryKey(
        variables.organizationId
      );
      const currentSetting =
        queryClient.getQueryData<ClientOrganizationPushNotificationSetting>(
          queryKey
        );

      if (currentSetting?.commentPushEnabled === variables.commentPushEnabled) {
        queryClient.invalidateQueries({ queryKey });
      }
    }
  });
};
